// UserProfileDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Button, Box, Typography,
    Alert, CircularProgress, Divider
} from '@mui/material';
import { API_BASE } from '../../apiBase';

export default function UserProfileDialog({ open, onClose, user, onUpdate }) {
    const [email, setEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (user && open) {
            setEmail(user.email || '');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setMessage('');
            setError('');
        }
    }, [user, open]);

    const handleSaveProfile = async () => {
        setMessage('');
        setError('');
        setLoading(true);

        try {
            const token = localStorage.getItem('token');

            // Update email
            const res = await fetch(`${API_BASE}/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ email })
            });

            const data = await res.json();

            if (!res.ok) {
                setError(data.error || 'Failed to update profile');
                setLoading(false);
                return;
            }

            setMessage('Profile updated successfully!');
            if (onUpdate) onUpdate(data);

            setTimeout(() => {
                setMessage('');
            }, 3000);
        } catch (err) {
            setError('Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async () => {
        setMessage('');
        setError('');

        if (!currentPassword) {
            setError('Current password is required');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (newPassword.length < 12) {
            setError('New password must be at least 12 characters');
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_BASE}/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword
                })
            });

            const data = await res.json();

            if (!res.ok) {
                setError(data.error || 'Failed to change password');
                setLoading(false);
                return;
            }

            setMessage('Password changed successfully!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');

            setTimeout(() => {
                setMessage('');
            }, 3000);
        } catch (err) {
            setError('Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>User Profile & Settings</DialogTitle>
            <DialogContent>
                {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Profile Information */}
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Profile Information</Typography>
                <TextField
                    label="Username"
                    value={user?.username || ''}
                    fullWidth
                    margin="dense"
                    disabled
                    helperText="Username cannot be changed"
                />
                <TextField
                    label="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    margin="dense"
                    type="email"
                />
                <Button
                    variant="contained"
                    onClick={handleSaveProfile}
                    disabled={loading || email === user?.email}
                    sx={{ mt: 1 }}
                >
                    {loading ? <CircularProgress size={24} /> : 'Update Email'}
                </Button>

                {/* MFA Status - Read-only, managed by admin */}
                {user?.mfa_enabled && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Multi-Factor Authentication:</strong> Enabled
                                <br />
                                A verification code will be sent to your email when you log in.
                                <br />
                                <em>MFA can only be managed by administrators.</em>
                            </Typography>
                        </Alert>
                    </>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Change Password */}
                <Typography variant="h6" sx={{ mb: 1 }}>Change Password</Typography>
                <TextField
                    label="Current Password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    fullWidth
                    margin="dense"
                />
                <TextField
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                    margin="dense"
                    helperText="Minimum 12 characters"
                />
                <TextField
                    label="Confirm New Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    margin="dense"
                />
                <Button
                    variant="contained"
                    onClick={handleChangePassword}
                    disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                    sx={{ mt: 1 }}
                >
                    {loading ? <CircularProgress size={24} /> : 'Change Password'}
                </Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
